import { styled } from "@mui/material/styles";
import { Button } from "@mui/material";
import { ICON_BUTTON_VARIANTS, ICON_BUTTON_SIZES, ICON_COLOR_MODES } from "./HyroIconButton.consts";

const getVariantStyles = (theme) => ({
  [ICON_BUTTON_VARIANTS.OUTLINED]: {
    color: theme.palette.system[700],
    border: "1px solid",
    borderColor: theme.palette.system[400],
    hoverColor: theme.palette.primary[500],
    hoverBackgroundColor: theme.palette.tertiary[300],
    hoverBorderColor: theme.palette.secondary[500],
    activeColor: theme.palette.primary[500],
    activeBackgroundColor: theme.palette.tertiary[300],
    activeBorderColor: theme.palette.secondary[500],
    disabledBorderColor: theme.palette.system[200],
    disabledColor: theme.palette.system[400],
    borderRadius: "8px"
  },
  [ICON_BUTTON_VARIANTS.CLEAR]: {
    color: theme.palette.system[700],
    hoverColor: theme.palette.system[700],
    hoverBackgroundColor: theme.palette.tertiary[300],
    activeColor: theme.palette.primary[500],
    activeBackgroundColor: "transparent",
    disabledColor: theme.palette.system[400],
    borderRadius: "50%"
  }
});

const getSizeStyles = () => ({
  [ICON_BUTTON_VARIANTS.CLEAR]: {
    [ICON_BUTTON_SIZES.SMALL]: {
      buttonSize: "24px",
      svgSize: "12px",
      strokeWidth: "1.4"
    },
    [ICON_BUTTON_SIZES.MEDIUM]: {
      buttonSize: "28px",
      svgSize: "14px",
      strokeWidth: "1.6"
    },
    [ICON_BUTTON_SIZES.LARGE]: {
      buttonSize: "32px",
      svgSize: "16px",
      strokeWidth: "1.8"
    },
    [ICON_BUTTON_SIZES.XLARGE]: {
      buttonSize: "34px",
      svgSize: "20px",
      strokeWidth: "1.8"
    }
  },
  [ICON_BUTTON_VARIANTS.OUTLINED]: {
    [ICON_BUTTON_SIZES.SMALL]: {
      buttonSize: "32px",
      svgSize: "20px",
      strokeWidth: "1.4"
    },
    [ICON_BUTTON_SIZES.MEDIUM]: {
      buttonSize: "36px",
      svgSize: "20px",
      strokeWidth: "1.6"
    },
    [ICON_BUTTON_SIZES.LARGE]: {
      buttonSize: "40px",
      svgSize: "24px",
      strokeWidth: "1.8"
    }
  }
});

const StyledIconButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== "active" && prop !== "iconColorMode" && prop !== "hasHoverStyle"
})(({ theme, size, variant, active, iconColorMode, hasHoverStyle }) => {
  const variantStyle = getVariantStyles(theme)[variant];
  const sizeStyle = getSizeStyles()[variant][size];

  return {
    ...variantStyle,
    ...sizeStyle,
    minWidth: "unset",
    padding: theme.spacing(0.5),
    backgroundColor: "transparent",
    width: sizeStyle.buttonSize,
    height: sizeStyle.buttonSize,
    aspectRatio: "1",
    "& > svg": {
      width: sizeStyle.svgSize,
      height: sizeStyle.svgSize,
      "& path": {
        strokeWidth: sizeStyle.strokeWidth,
        ...(iconColorMode === ICON_COLOR_MODES.FILL && {
          fill: "currentColor"
        }),
        ...(iconColorMode === ICON_COLOR_MODES.STROKE && {
          stroke: "currentColor"
        })
      }
    },
    ...(hasHoverStyle && {
      "&:hover": {
        backgroundColor: variantStyle.hoverBackgroundColor,
        borderColor: variantStyle.hoverBorderColor,
        color: variantStyle.hoverColor
      }
    }),
    "&:focus-visible": {
      outline: `1px solid ${theme.palette.primary[500]}`,
      outlineOffset: "2px",
      boxShadow: "none",
      "& .MuiTouchRipple-root": {
        display: "none"
      }
    },
    ...(active && {
      backgroundColor: variantStyle.activeBackgroundColor,
      borderColor: variantStyle.activeBorderColor,
      color: variantStyle.activeColor
    })
  };
});

export default StyledIconButton;
